import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUseCaseConfig } from "./useUseCaseConfig";
import { UseCaseConfig, DATA_SOURCE_LABELS, COMPARISON_MODE } from "../shared/types";
import * as S from "../shared/styles";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

type FilterKey = "all" | "active" | "inactive";

const FILTERS: FilterKey[] = ["all", "active", "inactive"];

// Temporarily hidden until the filter and create-use-case flows are built.
const SHOW_CONFIG_CONTROLS = false;

// ----------------------------  Status Badge  ----------------------------

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span style={status === "active" ? S.configStatusActive : S.configStatusInactive}>
    {status === "active" ? "Active" : "Inactive"}
  </span>
);

// ----------------------------  Main Page  ----------------------------

const UseCaseList: React.FC = () => {
  const navigate = useNavigate();
  const { configs, isLoading, isError } = useUseCaseConfig();
  const [filter, setFilter] = useState<FilterKey>("all");

  const shown = useMemo(
    () => configs.filter((u) => (filter === "all" ? true : u.status === filter)),
    [configs, filter],
  );

  if (isLoading) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Failed to load use case configurations. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={S.pageTitle}>Use Case Management</h1>
        <p style={S.pageSubtitle}>Create, activate, and edit audit use cases.</p>
      </div>

      {/* Filter + Create button — hidden until those flows are built */}
      {SHOW_CONFIG_CONTROLS && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={
                  filter === f
                    ? {
                        ...S.btnPrimary,
                        padding: "8px 16px",
                        fontSize: "0.875rem",
                        textTransform: "capitalize",
                      }
                    : {
                        ...S.btnSecondary,
                        padding: "8px 16px",
                        fontSize: "0.875rem",
                        textTransform: "capitalize",
                      }
                }
              >
                {f}
              </button>
            ))}
          </div>
          <button style={S.btnPrimary} onClick={() => navigate("/vlmAudit/config/new")}>
            + Create New Use Case
          </button>
        </div>
      )}

      {/* List */}
      <div style={S.taskList}>
        {shown.length === 0 && <div style={S.emptyState}>No use cases in this view.</div>}
        {shown.map((uc: UseCaseConfig) => (
          <div key={uc.id} style={S.configCard}>
            <div style={S.configCardRow}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontWeight: 600, fontSize: "1.125rem", color: "#111827" }}>
                    {uc.name || "(Untitled)"}
                  </span>
                  <StatusBadge status={uc.status} />
                  <span style={S.configCategoryBadge}>{uc.category}</span>
                </div>
                {uc.description && (
                  <p style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "4px" }}>
                    {uc.description}
                  </p>
                )}
                <div style={S.configMeta}>
                  <span>Source: {DATA_SOURCE_LABELS[uc.data_source_type]}</span>
                  <span>
                    Mode: {uc.comparison_mode === COMPARISON_MODE.OVERRIDE ? "Override" : "Simple"}
                  </span>
                  <span>Inter-rater: {uc.inter_rater_count}</span>
                  <span>Claim expiry: {uc.claim_expiry_minutes} min</span>
                  <span>Fields: {uc.fields.length}</span>
                  <span>Checklist: {uc.checklist_items.length}</span>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginLeft: "16px",
                  flexShrink: 0,
                }}
              >
                <button style={S.btnPrimary} onClick={() => navigate("/vlmAudit/config/" + uc.id)}>
                  Configure
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UseCaseList;
